/**
 * Read every row of a query, not just the first page.
 *
 * Supabase caps a response at 1,000 rows whether or not the code says
 * .limit(), so a query with no limit still quietly stops there. Anything that
 * then counts, ranks or percentages the result is describing the first page
 * and presenting it as the whole table — the failure mode is that the numbers
 * look completely reasonable and are wrong.
 *
 * Use this only for aggregates that cannot be done as an exact count. If the
 * answer is a single number, `select('*', { count: 'exact', head: true })` is
 * one round trip and always correct; this is for the cases that need the rows
 * themselves, like ranking tropes by how often they appear.
 *
 * `orderColumn` has to be unique, or .range() paging can skip and repeat rows
 * across pages. Primary keys are the safe choice.
 *
 * TWO FUNCTIONS, AND THE DIFFERENCE MATTERS
 *
 * fetchAllRows() logs a failure and hands back whatever it had. That is fine
 * for a count on a dashboard, where a wrong number is visibly a wrong number.
 * It is NOT fine when the caller renders the result as a list of choices:
 * there, a failed request and a genuinely empty table produce the identical
 * empty array, and an empty list of choices reads as "there is nothing to pick
 * here" rather than "this did not load". Nobody investigates that, which is
 * exactly what makes it dangerous.
 *
 * fetchAllRowsResult() returns { ok, rows, error } so the two are impossible
 * to confuse. Use it anywhere the empty case is shown to a person.
 */
const PAGE = 1000;
const MAX_PAGES = 100; // 100k rows — a stop so a bad call cannot loop forever

export async function fetchAllRowsResult(buildQuery, { orderColumn = 'id', pageSize = PAGE } = {}) {
  const rows = [];

  for (let page = 0; page < MAX_PAGES; page++) {
    const from = page * pageSize;
    const { data, error } = await buildQuery()
      .order(orderColumn, { ascending: true })
      .range(from, from + pageSize - 1);

    // Rows already read are handed back alongside the failure rather than
    // thrown away, so a caller that can show a partial list still can — but it
    // is told, and it decides.
    if (error) return { ok: false, rows, error: error.message };
    if (!data?.length) break;

    rows.push(...data);
    if (data.length < pageSize) break;
  }

  return { ok: true, rows, error: null };
}

export async function fetchAllRows(buildQuery, options) {
  const { ok, rows, error } = await fetchAllRowsResult(buildQuery, options);
  if (!ok) console.error('fetchAllRows:', error);
  return rows;
}
